import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ClipboardDocumentIcon, KeyIcon, XMarkIcon } from '@heroicons/react/24/outline';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

export default function PacerAuthenticatorButton() {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [expiresAt, setExpiresAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const fetchingRef = useRef(false);

  const loadCode = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    setError('');
    setCopied(false);
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(`${API_BASE}/firm/cmecf-authenticator-code/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        cache: 'no-store',
        signal: controller.signal,
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.detail || 'The PACER authenticator code could not be generated.');
      }
      setCode(String(payload.code || '').padStart(6, '0'));
      setExpiresAt(Date.now() + Math.max(1, Number(payload.seconds_remaining || 1)) * 1000);
      setNow(Date.now());
    } catch (requestError) {
      setCode('');
      setExpiresAt(0);
      setError(
        requestError.name === 'AbortError'
          ? 'The PACER code request timed out. Please try again.'
          : requestError.message || 'The PACER authenticator code could not be generated.',
      );
    } finally {
      window.clearTimeout(timeoutId);
      fetchingRef.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setCode('');
      setExpiresAt(0);
      setError('');
      return undefined;
    }
    loadCode();
    return undefined;
  }, [loadCode, open]);

  useEffect(() => {
    if (!open || !expiresAt) return undefined;
    const ticker = window.setInterval(() => setNow(Date.now()), 250);
    const refresh = window.setTimeout(() => loadCode(), Math.max(250, expiresAt - Date.now() + 150));
    return () => {
      window.clearInterval(ticker);
      window.clearTimeout(refresh);
    };
  }, [expiresAt, loadCode, open]);

  const secondsRemaining = expiresAt ? Math.max(0, Math.ceil((expiresAt - now) / 1000)) : 0;
  const progress = Math.max(0, Math.min(100, (secondsRemaining / 30) * 100));
  const formattedCode = useMemo(
    () => (code.length === 6 ? `${code.slice(0, 3)} ${code.slice(3)}` : code),
    [code],
  );

  async function copyCode() {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (_error) {
      window.prompt('Copy this PACER authenticator code:', code);
    }
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
      >
        <KeyIcon className="h-5 w-5 text-indigo-600" aria-hidden="true" />
        <span className="hidden sm:inline">PACER Code</span>
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 rounded-lg border border-gray-200 bg-white p-4 shadow-xl">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">PACER authenticator code</h3>
              <p className="mt-1 text-xs text-gray-500">Generated from the firm’s encrypted CM/ECF settings.</p>
            </div>
            <button type="button" onClick={() => setOpen(false)} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Close PACER code">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {loading && !code ? (
            <div className="mt-5 rounded-md bg-gray-50 px-4 py-6 text-center text-sm text-gray-600">Generating current code…</div>
          ) : error ? (
            <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">
              {error}
              <div className="mt-2"><a href="/account" className="font-semibold underline">Open Account Management</a></div>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={copyCode}
                className="mt-4 flex w-full items-center justify-between rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-4 text-left hover:bg-indigo-100"
              >
                <span className="font-mono text-3xl font-bold tracking-[0.16em] text-indigo-950">{formattedCode}</span>
                <span className="flex items-center gap-1 text-xs font-semibold text-indigo-700">
                  <ClipboardDocumentIcon className="h-5 w-5" />
                  {copied ? 'Copied' : 'Copy'}
                </span>
              </button>
              <div className="mt-3 flex items-center justify-between text-xs text-gray-600">
                <span>{loading ? 'Refreshing…' : `Valid for ${secondsRemaining} second${secondsRemaining === 1 ? '' : 's'}`}</span>
                <button type="button" onClick={loadCode} disabled={loading} className="font-semibold text-indigo-700 hover:text-indigo-900 disabled:opacity-50">Refresh</button>
              </div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-200">
                <div className="h-full rounded-full bg-indigo-600 transition-[width] duration-200" style={{ width: `${progress}%` }} />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
